import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import KnowledgeBaseList from "@/components/profile/KnowledgeBaseList";
import ActiveContributorsList from "@/components/profile/ActiveContributorsList";
import { useMarketplaceMetrics } from "@/hooks/useMarketplaceMetrics";

export interface Department {
  name: string;
  participants: number;
}

export interface NetworkStatsProps {
  totalParticipants: number;
  activeContributors: number;
  knowledgeBases: number;
  totalSolutions: number;
  departments: Department[];
  channelName: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const getMarketplaceName = (channelName: string) => {
  const names: Record<string, string> = {
    'curt': 'CURT DEMO Marketplace',
    'oil-gas': 'Oil & Gas DEMO Marketplace',
    'ispai': 'iSPAI Marketplace',
    'gsa': 'Federal DEMO Marketplace'
  };
  return names[channelName] || `${channelName.toUpperCase()} Marketplace`;
};

const KnowledgeNetworkStats = ({
  totalParticipants,
  activeContributors,
  knowledgeBases,
  totalSolutions,
  departments,
  channelName
}: NetworkStatsProps) => {
  const [isDistributionOpen, setIsDistributionOpen] = useState(false);
  const { data: marketplaceMetrics } = useMarketplaceMetrics();
  
  const isIspai = channelName === 'ispai';
  const isDemo = channelName === 'curt' || channelName === 'oil-gas';
  
  return <div className="flex flex-col gap-6">
      <Card className="p-6 bg-white shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Network Participants</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-gray-600 mb-1">Total mySPAI Profiles</p>
            <p className="text-3xl font-bold text-primary">{totalParticipants}</p>
          </div>
          <HoverCard>
            <HoverCardTrigger asChild>
              <div className="text-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]">
                <p className="text-gray-600 mb-1">Active Contributors</p>
                <p className="text-3xl font-bold text-primary">{activeContributors}</p>
              </div>
            </HoverCardTrigger>
            <HoverCardContent side="left" align="end" className="w-[600px] max-h-[600px] overflow-hidden p-0">
              <ActiveContributorsList channelName={channelName} />
            </HoverCardContent>
          </HoverCard>
          <HoverCard>
            <HoverCardTrigger asChild>
              <div className="text-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]">
                <p className="text-gray-600 mb-1">Knowledge Bases</p>
                <p className="text-3xl font-bold text-primary">{knowledgeBases}</p>
              </div>
            </HoverCardTrigger>
            <HoverCardContent side="left" align="end" className="w-[600px] max-h-[600px] overflow-hidden p-0">
              <KnowledgeBaseList channelName={channelName} />
            </HoverCardContent>
          </HoverCard>
          {isIspai && marketplaceMetrics ? (
            <HoverCard>
              <HoverCardTrigger asChild>
                <Link 
                  to="/ispai-solutions" 
                  className="text-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] flex flex-col items-center justify-center group"
                >
                  <p className="text-gray-600 mb-1 group-hover:font-medium transition-all">{getMarketplaceName(channelName)}</p>
                  <p className="text-3xl font-bold text-primary">{totalSolutions}</p>
                </Link>
              </HoverCardTrigger>
              <HoverCardContent side="left" align="end" className="w-80">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold text-lg">Marketplace Metrics</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Knowledge Bases</p>
                      <p className="text-2xl font-bold text-primary">{marketplaceMetrics.totalKBs}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Solutions</p>
                      <p className="text-2xl font-bold text-primary">{marketplaceMetrics.totalSolutions}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Total Value Locked</p>
                      <p className="text-2xl font-bold text-green-600">${marketplaceMetrics.totalValueLocked}</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Total Rewards</p>
                      <p className="text-2xl font-bold text-purple-600">${marketplaceMetrics.totalRewards}</p>
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <Link 
              to={`/channel-solutions?channel=${channelName}${isDemo ? '&demo=true' : ''}`} 
              className="text-center p-4 bg-black rounded-lg cursor-pointer hover:bg-gray-800 transition-colors flex flex-col items-center justify-center group"
            >
              <p className="text-white mb-1 group-hover:font-medium transition-all">{getMarketplaceName(channelName)}</p>
              <p className="text-3xl font-bold text-white">{totalSolutions}</p>
            </Link>
          )}
        </div>
      </Card>
      
      <Collapsible open={isDistributionOpen} onOpenChange={setIsDistributionOpen}>
        <Card className="p-6 bg-white shadow-sm flex flex-col">
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Participant Distribution</h3>
              <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isDistributionOpen ? 'rotate-180' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-grow">
              <div className="w-1/2 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={departments} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="participants">
                      {departments.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={value => [`${value} participants`, '']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2">
                <ul className="space-y-2">
                  {departments.map((dept, index) => <li key={dept.name} className="flex items-center text-sm">
                      <span className="w-3 h-3 rounded-full mr-2" style={{
                    backgroundColor: COLORS[index % COLORS.length]
                  }}></span>
                      <span className="flex-1">{dept.name}</span>
                      <span className="font-medium">{dept.participants}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>;
};

export default KnowledgeNetworkStats;
